// Sign-in page (Cognito SRP). Requirement 13.6.
//
// Sign-up is self-service (the "Create one" link renders Signup), so most users arrive
// with a password they already set and are driven straight into mandatory TOTP enrollment.
// An admin-created (invited) user instead gets a temporary password and hits the
// NEW_PASSWORD_REQUIRED challenge on first sign-in, which this page also handles.

import { useMemo, useState, type FormEvent } from "react";

import { ApiClient, apiBaseUrl } from "../api/client";
import { totpErrorMessage } from "../auth/cognito";
import { useAuth } from "../auth/useAuth";
import { Signup } from "./Signup";
import { BrandMark } from "../components/BrandMark";
import { TotpQrCode } from "../components/TotpQrCode";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function Login() {
  const { signIn, pendingNewPassword, pendingTotp, pendingTotpSetup } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  // Toggle between the sign-in and self-service sign-up views. A brand-new user picks
  // "Create one" to register; after verifying their email they land back on sign-in.
  const [mode, setMode] = useState<"signin" | "signup">("signin");

  // Self-service MFA reset (for a user who lost their authenticator). "request" emails a
  // code; "confirm" takes the code + password to clear TOTP. These endpoints are public,
  // so a bare client with no bearer is used.
  const publicClient = useMemo(() => new ApiClient(apiBaseUrl(), () => Promise.resolve(null)), []);
  const [resetMode, setResetMode] = useState<"off" | "request" | "confirm" | "done">("off");
  const [resetCode, setResetCode] = useState("");

  const startMfaReset = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    setBusy(true);
    try {
      await publicClient.startMfaReset(email.trim());
      setResetMode("confirm");
      setStatus("If that account exists, we emailed a reset code. Enter it with your password.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not start the reset");
    } finally {
      setBusy(false);
    }
  };

  const confirmMfaReset = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    setBusy(true);
    try {
      await publicClient.confirmMfaReset(email.trim(), resetCode.trim(), password);
      setResetMode("done");
      setStatus("");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "The code or password is incorrect.");
    } finally {
      setBusy(false);
    }
  };

  // Guard the authenticator code before it reaches Cognito, so an empty or malformed
  // entry shows a friendly message instead of the raw "Missing required parameter
  // SOFTWARE_TOKEN_MFA_CODE" API error.
  const validCode = (): string | null => {
    const code = mfaCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setStatus("Enter the 6-digit code from your authenticator app.");
      return null;
    }
    return code;
  };

  const submitTotp = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    const code = validCode();
    if (!code) return;
    setBusy(true);
    try {
      await pendingTotp?.submit(code);
    } catch (err) {
      setStatus(totpErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitTotpSetup = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    const code = validCode();
    if (!code) return;
    setBusy(true);
    try {
      await pendingTotpSetup?.verify(code);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not verify the code");
    } finally {
      setBusy(false);
    }
  };

  const submitSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      // On success the provider flips isAuthenticated and the guard swaps the view.
      // If a new password is required, pendingNewPassword renders the next step below.
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    if (newPassword !== confirmPassword) {
      setStatus("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await pendingNewPassword?.complete(newPassword);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not set the new password");
    } finally {
      setBusy(false);
    }
  };

  // Self-service sign-up view. Only shown when no auth challenge is mid-flight, so a
  // started sign-in (MFA/new-password) always takes precedence.
  if (
    mode === "signup" &&
    !pendingTotpSetup &&
    !pendingTotp &&
    !pendingNewPassword
  ) {
    return (
      <section className="space-y-3">
        <Signup
          onSignedUp={(e) => {
            setEmail(e);
            setMode("signin");
            setStatus("Account verified. Sign in to finish setting up two-factor auth.");
          }}
        />
        <p className="mx-auto max-w-4xl text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setMode("signin")}
          >
            Sign in
          </button>
        </p>
      </section>
    );
  }

  if (pendingTotpSetup) {
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Set up two-factor authentication</CardTitle>
            <CardDescription>
              Scan the QR code, then enter the 6-digit authenticator code.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 space-y-3">
              <TotpQrCode uri={pendingTotpSetup.otpauthUri} />
              <div className="space-y-2">
                <Label htmlFor="totp-secret">Setup key</Label>
                <Input
                  id="totp-secret"
                  readOnly
                  value={pendingTotpSetup.secretCode}
                  aria-label="totp-secret"
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <p className="text-xs text-muted-foreground">
                  Can't scan? Enter this key in your authenticator app manually.
                </p>
              </div>
            </div>
            <form className="space-y-4" onSubmit={(e) => void submitTotpSetup(e)}>
              <div className="space-y-2">
                <Label htmlFor="mfa-setup-code">Authenticator code</Label>
                <Input
                  id="mfa-setup-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  aria-label="mfa-setup-code"
                  placeholder="123456"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Verifying…" : "Verify and continue"}
              </Button>
            </form>
            {status && <p className="status mt-3 text-sm text-destructive">{status}</p>}
          </CardContent>
        </Card>
      </section>
    );
  }

  if (resetMode === "done") {
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Two-factor authentication reset</CardTitle>
            <CardDescription>
              Reset complete. Sign in to set up a new authenticator. Your data is unchanged.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              className="w-full"
              onClick={() => {
                setResetMode("off");
                setStatus("");
                setResetCode("");
                setPassword("");
              }}
            >
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (resetMode === "request" || resetMode === "confirm") {
    const requesting = resetMode === "request";
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Reset two-factor authentication</CardTitle>
            <CardDescription>
              {requesting
                ? "Lost your authenticator? Enter your email and we'll send a reset code."
                : "Enter the code we emailed, plus your password, to reset two-factor auth."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(e) => void (requesting ? startMfaReset(e) : confirmMfaReset(e))}
            >
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="reset-email"
                  autoComplete="username"
                  disabled={!requesting}
                />
              </div>
              {!requesting && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="reset-code">Reset code</Label>
                    <Input
                      id="reset-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      aria-label="reset-code"
                      placeholder="123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-password">Password</Label>
                    <Input
                      id="reset-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-label="reset-password"
                      autoComplete="current-password"
                    />
                  </div>
                </>
              )}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy
                  ? "Working…"
                  : requesting
                    ? "Send reset code"
                    : "Reset two-factor auth"}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => {
                  setResetMode("off");
                  setStatus("");
                  setResetCode("");
                }}
              >
                Back to sign in
              </button>
            </p>
            {status && <p className="status mt-3 text-sm text-muted-foreground">{status}</p>}
          </CardContent>
        </Card>
      </section>
    );
  }

  if (pendingTotp) {
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Two-factor authentication</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={(e) => void submitTotp(e)}>
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Authenticator code</Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  aria-label="mfa-code"
                  placeholder="123456"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Verifying…" : "Verify"}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => {
                  setStatus("");
                  setResetMode("request");
                }}
              >
                Lost your authenticator?
              </button>
            </p>
            {status && <p className="status mt-3 text-sm text-destructive">{status}</p>}
          </CardContent>
        </Card>
      </section>
    );
  }

  if (pendingNewPassword) {
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>
              Your account requires a permanent password before continuing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={(e) => void submitNewPassword(e)}>
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  aria-label="new-password"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  aria-label="confirm-password"
                  autoComplete="new-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Saving…" : "Set password"}
              </Button>
            </form>
            {status && <p className="status mt-3 text-sm text-destructive">{status}</p>}
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
      <Card className="w-full">
        <CardHeader className="items-center text-center">
          <BrandMark className="mb-1 size-10" />
          <CardTitle>
            Sign in to smart<span className="text-primary">-margin</span>
          </CardTitle>
          <CardDescription>Access your dashboard.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(e) => void submitSignIn(e)}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="email"
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label="password"
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          {status && <p className="status mt-3 text-sm text-destructive">{status}</p>}
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => {
                setStatus("");
                setMode("signup");
              }}
            >
              Create one
            </button>
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
